import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Spin } from 'antd'
import { includes, isEmpty, isNil } from 'lodash'

import { showErrorNotification } from '../../../core/helpers'

import {
  CurationTaskAwaitingModal,
  Page,
} from '../../../common/components/sections'
import { StyledPageHeader } from '../../../common/components/styled'
import { APP_ROUTES } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks'

import { LocationsTable } from '../../../locations/components/sections'
import { BRAND_CURATION_TASK_STATUS } from '../../constants'
import {
  useBrandCurationTaskQuery,
  useUnblockBrandCurationTaskSubscription,
  useUpdateBrandCurationTaskMutation,
  useUserHasBrandCurationTaskChangePermission,
} from '../../hooks'
import {
  BrandCurationTaskEditorForm,
  BrandCurationTaskTagsForm,
} from '../forms'
import {
  BrandCurationTaskDetails,
  BrandCurationTaskHeader,
  BrandCurationTaskMenuButtons,
} from '../sections'

const BrandCurationTaskPage = () => {
  const { id } = useParams()
  useDocumentTitle(APP_ROUTES.BRANDS.label)

  const {
    mainLocationTypeOptions,
    additionalLocationTypeOptions,
    cuisineTypeOptions,
  } = useSelector(store => store.common.metadata.data)

  const [
    updateBrandCurationTask,
    { isLoading: isUpdateLoading, error: updateError },
  ] = useUpdateBrandCurationTaskMutation()

  const {
    data: brandCurationTask,
    error,
    isLoading,
    refetch,
  } = useBrandCurationTaskQuery(id)

  useUnblockBrandCurationTaskSubscription(refetch)

  const { userHasBrandChangePermission, isUserQc } =
    useUserHasBrandCurationTaskChangePermission(brandCurationTask)

  const isCurationDisabled = useMemo(
    () =>
      isUserQc
        ? !userHasBrandChangePermission
        : !userHasBrandChangePermission ||
          !userHasBrandChangePermission ||
          includes(
            [
              BRAND_CURATION_TASK_STATUS.RESOLVED.value,
              BRAND_CURATION_TASK_STATUS.AUTOMATICALLY_RESOLVED.value,
              BRAND_CURATION_TASK_STATUS.NOT_STARTED.value,
            ],
            brandCurationTask?.taskStatus,
          ),
    [brandCurationTask?.taskStatus, isUserQc, userHasBrandChangePermission],
  )

  useEffect(() => {
    if (error || updateError) {
      showErrorNotification({
        title: !isNil(error)
          ? 'Failed to get brand curation task'
          : 'Failed to update brand curation task',
        description: error || updateError,
      })
    }
  }, [error, updateError])

  const handleSubmit = useCallback(
    formData => {
      updateBrandCurationTask(id, formData)
    },
    [id, updateBrandCurationTask],
  )

  if (isEmpty(brandCurationTask) || isLoading) {
    return <Spin />
  }

  return (
    <Page>
      <StyledPageHeader sticky>
        <BrandCurationTaskMenuButtons brandCurationTask={brandCurationTask} />
      </StyledPageHeader>
      <CurationTaskAwaitingModal
        status={brandCurationTask?.awaitingStatus}
        redirectTo={APP_ROUTES.BRANDS.fullPath}
      />
      <BrandCurationTaskHeader brandCurationTask={brandCurationTask} />
      <BrandCurationTaskTagsForm
        brandCurationTask={brandCurationTask}
        onSubmit={handleSubmit}
        isLoading={isUpdateLoading}
        isDisabled={isCurationDisabled}
      />
      <BrandCurationTaskEditorForm
        brandCurationTask={brandCurationTask}
        mainLocationTypeOptions={mainLocationTypeOptions}
        additionalLocationTypeOptions={additionalLocationTypeOptions}
        cuisineTypeOptions={cuisineTypeOptions}
        onSubmit={handleSubmit}
        isLoading={isUpdateLoading}
        isDisabled={isCurationDisabled}
      />
      <BrandCurationTaskDetails brandCurationTask={brandCurationTask} />
      <LocationsTable
        brandCurationTask={brandCurationTask}
        isDisabled={isCurationDisabled}
      />
    </Page>
  )
}

export default BrandCurationTaskPage
